import { IGetParams } from '@/api/types';

import {
  TableFilterInputType,
  TableGridFilterModel,
  FilterRow,
} from '../types';

/**
 * Validates that a string is a valid date
 * @param value The date string to validate
 * @returns boolean indicating if the date is valid
 */
export const validateDate = (value: string, allowEmpty = false): boolean => {
  if (allowEmpty && !value) {
    return true;
  }

  // Check if it's a valid date
  if (isNaN(Date.parse(value))) {
    return false;
  }

  // Additional check to ensure it's a date format (not just a number)
  // This regex checks for common date separators (-, /, .)
  // eslint-disable-next-line no-useless-escape
  const dateRegex = /\d{1,4}[-/\.]\d{1,2}[-/\.]\d{1,4}/;
  return dateRegex.test(value);
};

/**
 * Validates that a datetime string contains both date and time components with minutes and seconds
 * @param value The datetime string to validate
 * @returns boolean indicating if the datetime is valid
 */
export const validateDateTime = (
  value: string,
  allowEmpty = false,
): boolean => {
  if (allowEmpty && !value) {
    return true;
  }

  // First check if it's a valid date
  if (isNaN(Date.parse(value))) {
    return false;
  }

  // Check if it contains time with minutes and seconds
  // Valid formats should include HH:mm:ss
  const timeRegex = /\d{1,2}[:/.]\d{1,2}[:/.]\d{1,2}/;
  return timeRegex.test(value);
};

export const validateFilterInputByType = (
  inputType: TableFilterInputType,
  value: string,
) => {
  switch (inputType) {
    case 'text':
      // allow empty values
      if (value === null || value === undefined || value === '') {
        return true;
      }
      return value && value.trim().length > 0;
    case 'number':
      // allow empty values
      if (value === null || value === undefined || value === '') {
        return true;
      }
      return !isNaN(Number(value));
    case 'date':
      return validateDate(value);
    case 'boolean':
      return value === 'true' || value === 'false' || value === 'any';
    case 'datetime':
      return validateDateTime(value);
    case 'list':
      return value && value.trim().length > 0;
    default:
      return false;
  }
};

export const filterValuesAreValid = (filters: FilterRow[]) => {
  return filters.every(
    (filter) =>
      // chcek column
      filter.column &&
      filter.column !== '' &&
      filter.column !== null &&
      filter.column !== undefined &&
      // check operator
      filter.operator &&
      filter.operator !== '' &&
      filter.operator !== null &&
      filter.operator !== undefined &&
      // check value
      validateFilterInputByType(filter.inputType, filter.value),
  );
};

export const isSearchFilterTextEmpty = (query: IGetParams) => {
  return (
    (query.filter as TableGridFilterModel)?.quickFilterValues?.length === 0
  );
};
